import { startTransition, useEffect, useState } from 'react';
import { useEditEndpointOIDCProvider } from '#api/endpoint-oidc-providers.queries.ts';
import { trackError } from '#api/errors.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { FilterPill } from '#components/atoms/pill/filter-pill/FilterPill.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import { EndpointOIDCProviderCard } from '../detail/endpoint/EndpointOIDCProviderCard.tsx';
import { EmptyEndpointIdentityProvider } from '../sso-page/empty/EmptyEndpointIdentityProvider.tsx';
import { EnablePromptBanner } from './EnablePromptBanner.tsx';
import { useIssuerURLFilter } from './useIssuerURLFilter.ts';
import { useShowEnableBannerForProvider } from './useShowEnableBanner.ts';

type Props = {
  idps: EndpointOIDC[];
};

export function EndpointOIDCList({ idps }: Props) {
  const [globalErrorMsg, setGlobalErrorMsg] = useState<string | null>(null);
  const [issuerURL, setIssuerURL] = useIssuerURLFilter();
  const filteredIdps = issuerURL ? idps.filter((p) => p.issuerURL === issuerURL) : idps;
  const {
    providerId: enableBannerProviderId,
    provider: providerToPromptEnable,
    domain,
    clearProviderId,
  } = useShowEnableBannerForProvider();
  const editOIDCProviderMutation = useEditEndpointOIDCProvider();

  async function enablePromptedProvider(provider: EndpointOIDC) {
    try {
      await editOIDCProviderMutation.mutateAsync({
        id: provider.id,
        values: { clientSecret: null, ...provider, enabled: true },
      });
    } catch (err) {
      trackError(err);
      setGlobalErrorMsg('Could not enable provider, please refresh and try again');
    }
  }

  // Clear the enable banner once the provider is enabled or deleted
  useEffect(() => {
    if (!enableBannerProviderId) return;
    const match = idps.find((p) => p.id === enableBannerProviderId);
    if (!match || match.enabled) {
      clearProviderId();
    }
  }, [idps, enableBannerProviderId, clearProviderId]);

  return (
    <>
      {globalErrorMsg ?
        <ErrorMessage className="mb-16">{globalErrorMsg}</ErrorMessage>
      : null}
      {providerToPromptEnable && domain ?
        <EnablePromptBanner
          className="mb-40"
          providerName={providerToPromptEnable.name}
          domain={domain}
          isEnabling={editOIDCProviderMutation.isPending}
          onEnable={() => {
            startTransition(() => enablePromptedProvider(providerToPromptEnable));
          }}
          onDismiss={clearProviderId}
        />
      : null}
      {issuerURL ?
        <div className="mb-24">
          <FilterPill label={`Issuer URL: ${issuerURL}`} onClick={() => setIssuerURL(undefined)} />
        </div>
      : null}
      {filteredIdps.length ?
        <>
          <h2 className="h4">Providers</h2>
          <Spacer direction="vertical" className="mb-24">
            {filteredIdps.map((provider) => {
              return (
                <EndpointOIDCProviderCard
                  key={provider.id}
                  endpointOIDC={provider}
                  setGlobalErrorMsg={setGlobalErrorMsg}
                />
              );
            })}
          </Spacer>
        </>
      : issuerURL ?
        <p className="p-lt mb-24">No endpoint providers use this issuer URL.</p>
      : <EmptyEndpointIdentityProvider className="mb-24" />}
    </>
  );
}
